import {AdviceType, OrderAdvice} from "../../../models/OrderAdvice";

export interface AdviceChange {
    readonly type: AdviceChangeType;
}

export enum AdviceChangeType {
    ADVICE_MODEL_CHANGED,
    ADVISER_CHANGED,
    ORDER_TAKER_CHANGED,
    ORDER_PLACER_CHANGED,
    REFERENCE_CHANGED,
    ADVICE_METHOD_CHANGED,
    ADVICE_TYPE_CHANGED,
    ADVICE_CAPTURED_CHANGED,
    TOGGLE_DECLARATION,
    TOGGLE_BASIS,
    ADVICE_COMMEND_CHANGED
}

export interface AdviceModelChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVICE_MODEL_CHANGED;
    readonly advice: OrderAdvice;
}

export function adviceModelChange(advice: OrderAdvice): AdviceModelChange {
    return {
        type: AdviceChangeType.ADVICE_MODEL_CHANGED,
        advice
    };
}

export function isModelChange(change: AdviceChange): change is AdviceModelChange {
    return change.type === AdviceChangeType.ADVICE_MODEL_CHANGED;
}

export interface AdviceAdviserChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVISER_CHANGED;
    readonly adviser: string;
}

export function adviceAdviserChange(adviser: string): AdviceAdviserChange {
    return {
        type: AdviceChangeType.ADVISER_CHANGED,
        adviser
    };
}

export function isAdviserChange(change: AdviceChange): change is AdviceAdviserChange {
    return change.type === AdviceChangeType.ADVISER_CHANGED;
}

export interface AdviceTakerChange extends AdviceChange {
    readonly type: AdviceChangeType.ORDER_TAKER_CHANGED;
    readonly orderTaker: string;
}

export function adviceTakerChange(orderTaker: string): AdviceTakerChange {
    return {
        type: AdviceChangeType.ORDER_TAKER_CHANGED,
        orderTaker
    };
}

export function isTakerChange(change: AdviceChange): change is AdviceTakerChange {
    return change.type === AdviceChangeType.ORDER_TAKER_CHANGED;
}

export interface AdvicePlacerChange extends AdviceChange {
    readonly type: AdviceChangeType.ORDER_PLACER_CHANGED;
    readonly orderPlacer: string;
}

export function advicePlacerChange(orderPlacer: string): AdvicePlacerChange {
    return {
        type: AdviceChangeType.ORDER_PLACER_CHANGED,
        orderPlacer
    };
}

export function isPlacerChange(change: AdviceChange): change is AdvicePlacerChange {
    return change.type === AdviceChangeType.ORDER_PLACER_CHANGED;
}

export interface AdviceReferenceChange extends AdviceChange {
    readonly type: AdviceChangeType.REFERENCE_CHANGED;
    readonly reference: string;
}

export function adviceReferenceChange(reference: string): AdviceReferenceChange {
    return {
        type: AdviceChangeType.REFERENCE_CHANGED,
        reference
    };
}

export function isReferenceChange(change: AdviceChange): change is AdviceReferenceChange {
    return change.type === AdviceChangeType.REFERENCE_CHANGED;
}

export interface AdviceMethodChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVICE_METHOD_CHANGED;
    readonly method: string;
}

export function adviceMethodChange(method: string): AdviceMethodChange {
    return {
        type: AdviceChangeType.ADVICE_METHOD_CHANGED,
        method
    };
}

export function isMethodChange(change: AdviceChange): change is AdviceMethodChange {
    return change.type === AdviceChangeType.ADVICE_METHOD_CHANGED;
}

export interface AdviceTypeChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVICE_TYPE_CHANGED;
    readonly adviceType: AdviceType;
}

export function adviceTypeChange(adviceType: AdviceType): AdviceTypeChange {
    return {
        type: AdviceChangeType.ADVICE_TYPE_CHANGED,
        adviceType
    };
}

export function isTypeChange(change: AdviceChange): change is AdviceTypeChange {
    return change.type === AdviceChangeType.ADVICE_TYPE_CHANGED;
}

export interface AdviceCapturedChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVICE_CAPTURED_CHANGED;
    readonly captured: Date;
}

export function adviceCapturedChange(captured: Date): AdviceCapturedChange {
    return {
        type: AdviceChangeType.ADVICE_CAPTURED_CHANGED,
        captured
    };
}

export function isCapturedChange(change: AdviceChange): change is AdviceCapturedChange {
    return change.type === AdviceChangeType.ADVICE_CAPTURED_CHANGED;
}

export interface AdviceDeclarationToggleChange extends AdviceChange {
    readonly type: AdviceChangeType.TOGGLE_DECLARATION;
    readonly declaration: string;
}

export function adviceDeclarationToggleChange(declaration: string): AdviceDeclarationToggleChange {
    return {
        type: AdviceChangeType.TOGGLE_DECLARATION,
        declaration
    };
}

export function isDeclarationToggleChange(change: AdviceChange): change is AdviceDeclarationToggleChange {
    return change.type === AdviceChangeType.TOGGLE_DECLARATION;
}

export interface AdviceBasisToggleChange extends AdviceChange {
    readonly type: AdviceChangeType.TOGGLE_BASIS;
    readonly basis: string;
}

export function adviceBasisToggleChange(basis: string): AdviceBasisToggleChange {
    return {
        type: AdviceChangeType.TOGGLE_BASIS,
        basis
    };
}

export function isBasisToggleChange(change: AdviceChange): change is AdviceBasisToggleChange {
    return change.type === AdviceChangeType.TOGGLE_BASIS;
}

export interface AdviceCommentChange extends AdviceChange {
    readonly type: AdviceChangeType.ADVICE_COMMEND_CHANGED;
    readonly comment: string;
}

export function adviceCommentChange(comment: string): AdviceCommentChange {
    return {
        type: AdviceChangeType.ADVICE_COMMEND_CHANGED,
        comment
    };
}

export function isCommentChange(change: AdviceChange): change is AdviceCommentChange {
    return change.type === AdviceChangeType.ADVICE_COMMEND_CHANGED;
}
